import type { Layout, Step } from '@mado/schema';
import { viewportToAsset } from '@mado/schema/geometry';

/**
 * Misses: a tap or click on the stage that hit nothing.
 *
 * Everyone reports views and completion. A miss is the signal nobody else
 * has — the viewer expected something to happen *here* and it did not. Per
 * step, that is where a demo confuses people; in aggregate, on the step's own
 * screenshot, it is a heatmap of where the real target should have been.
 *
 * What counts as ONE interaction (CLAUDE.md rule 4: exactly one event each):
 *
 *  - A press and release on the stage that moved less than {@link SLOP} — the
 *    same tap-vs-drag threshold `gestures.ts` uses. A swipe, a scroll drag or
 *    a wheel is a gesture, never a miss, however far from a target it starts.
 *  - …that landed on the pixels, not on a control: hotspots, the chrome bar,
 *    the badge, the CTA, the end card and the intro/chapter cards are all
 *    excluded by DOM ancestry, so the check cannot drift out of step with
 *    what the player draws.
 *  - …on a step that has something to tap. On a step with nothing to tap a
 *    tap anywhere — letterbox, mid-transition, a quick second tap — is "go
 *    on" (`onTap`), never a miss.
 *  - …while the step is settled: nothing is counted mid-transition, while the
 *    intro cover owns input, or once the end card is up.
 *  - …inside the asset. A tap on the letterbox beside a fitted frame is not a
 *    tap on the product and has no place on the step's screenshot.
 *  - A burst of taps at the same spot is one interaction: a second press
 *    within {@link REPEAT_MS} of the last counted (or suppressed) miss and
 *    within {@link REPEAT_PX} of it is the same frantic double-tap, not a new
 *    decision. Move the finger, or pause, and the next tap counts again.
 *
 * The point is reported in normalized ASSET coordinates — the stage's layout
 * transform inverted, the same way `overlays.ts` projects hotspot geometry
 * forwards — rounded to three decimals. Sticky bands on a scroll step are
 * mapped to the rows of the tall image they actually show.
 */

export interface MissPoint {
  x: number;
  y: number;
}

/** Everything a miss needs from the player at the moment of release. */
export interface MissContext {
  step: Step;
  layout: Layout;
  asset: { w: number; h: number };
  viewport: { w: number; h: number };
}

export interface MissCallbacks {
  /** Current geometry, or null when a tap must not count right now. */
  context(): MissContext | null;
  onMiss(point: MissPoint): void;
  /**
   * Every tap on the stage that is not on a control, before anything else is
   * decided — letterbox, busy and repeat included. True when the player took
   * it as an action of its own (a step with nothing to tap goes on, a press
   * on a lit callout ring): it is then not a miss, and does not count
   * towards a burst. `x`, `y`: where, in viewport px.
   */
  onTap?(x: number, y: number): boolean;
}

/** Movement below this is a tap, not a drag — same slop as gestures.ts. */
const SLOP = 8;
/** A second tap this soon and this close to the last miss is the same interaction. */
export const REPEAT_MS = 300;
export const REPEAT_PX = 24;
/** Anything the viewer can legitimately act on. A tap on these is never a miss. */
const CONTROLS =
  '.mado-hotspot,.mado-chrome,.mado-cta,.mado-replay,.mado-card,.mado-caption,.mado-callout,button,a';

export class MissDetector {
  private down: { id: number | undefined; x: number; y: number } | null = null;
  private last: { x: number; y: number; t: number } | null = null;

  constructor(
    /** The viewport: taps are measured against it. */
    private readonly host: HTMLElement,
    private readonly cb: MissCallbacks,
    /**
     * Where taps are heard — the whole player, so a tap on the frame around
     * a letterboxed screen reaches `onTap` too. Outside the viewport it is
     * outside the asset, and never a miss.
     */
    listen: HTMLElement = host,
  ) {
    listen.addEventListener('pointerdown', this.onDown, { passive: true });
    listen.addEventListener('pointerup', this.onUp, { passive: true });
    listen.addEventListener('pointercancel', this.onCancel, { passive: true });
  }

  private skip = false;

  /**
   * The press being dispatched right now is a take-over of watch mode, not a
   * tap at something: it must not become a miss. Called from a capture-phase
   * listener above this detector, before `onDown` sees the same event.
   */
  skipPress(): void {
    this.skip = true;
  }

  private onDown = (e: PointerEvent): void => {
    if (this.skip) {
      this.skip = false;
      this.down = null;
      return;
    }
    this.down = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };

  /** The browser took the pointer for its own scrolling: not a tap. */
  private onCancel = (): void => {
    this.down = null;
  };

  private onUp = (e: PointerEvent): void => {
    const down = this.down;
    this.down = null;
    if (!down || down.id !== e.pointerId) return;
    if (Math.abs(e.clientX - down.x) >= SLOP || Math.abs(e.clientY - down.y) >= SLOP) return;

    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest(CONTROLS)) return;

    const box = this.host.getBoundingClientRect();
    const px = e.clientX - box.left;
    const py = e.clientY - box.top;

    if (this.cb.onTap?.(px, py)) {
      this.last = null;
      return;
    }

    const ctx = this.cb.context();
    if (!ctx) return;

    // Same spot, no pause since the last one: the same interaction. The
    // clock restarts so a burst is one miss however long it goes on.
    const now = e.timeStamp || performance.now();
    if (
      this.last &&
      now - this.last.t < REPEAT_MS &&
      Math.abs(px - this.last.x) < REPEAT_PX &&
      Math.abs(py - this.last.y) < REPEAT_PX
    ) {
      this.last.t = now;
      return;
    }

    const point = toAsset(px, py, ctx);
    if (!point) return;

    this.last = { x: px, y: py, t: now };
    this.cb.onMiss(point);
  };
}

/**
 * Viewport CSS px → normalized asset coordinates, or null outside the asset.
 * Exported for tests: the inversion has to agree with `projectRect`.
 */
export function toAsset(px: number, py: number, ctx: MissContext): MissPoint | null {
  const { layout, asset, step, viewport } = ctx;
  let p = viewportToAsset({ x: px, y: py }, layout, asset);

  // A scroll step re-overlays the top and bottom of the tall asset as sticky
  // bands (stage.ts `buildSticky`). A tap on a band is a tap on those rows of
  // the image, wherever the content underneath has scrolled to.
  const s = step.scroll;
  if (s) {
    const scaledH = asset.h * layout.scale;
    const top = s.sticky.top * scaledH;
    const bottom = s.sticky.bottom * scaledH;
    if (top > 0 && py < top) p = { x: p.x, y: py / scaledH };
    else if (bottom > 0 && py > viewport.h - bottom) {
      p = { x: p.x, y: 1 - (viewport.h - py) / scaledH };
    }
  }

  if (p.x < 0 || p.x > 1 || p.y < 0 || p.y > 1) return null;
  return { x: round3(p.x), y: round3(p.y) };
}

const round3 = (v: number): number => Math.round(v * 1000) / 1000;
